import { type BranchKey, useCatchupBranch } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { useRebaseWorkspace } from '../../../workspacesPanel/workspaceDetails/manageWorkspace/useRebaseWorkspace'

type Props = {
  branchKey: BranchKey
}

export const CatchupBranchButton = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const { mutateAsync: catchup, isPending: isCatchingUp } = useCatchupBranch()
  const invalidateBranch = useInvalidateBranchAfterHistoryRewrite()
  const currentConciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: branchKey.name,
    shouldFallbackToStandard: false,
  })
  const rebaseWorkspace = useRebaseWorkspace()
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const singleWorkspace =
    branchWorkspaces?.length === 1 ? branchWorkspaces[0] : undefined

  const handleCatchup = async () => {
    try {
      await catchup({ branchName: branchKey.name, server: branchKey.server })

      toast({
        title: translate('branchesTree.catchup.success', {
          branchName: branchKey.name,
          followsBranchName:
            currentConciseBranch?.followsBranchName ?? translate('none'),
        }),
        variant: 'success',
      })

      await invalidateBranch(branchKey)

      // Workspace update runs after branch invalidation; handles its own errors.
      if (singleWorkspace) {
        const workspaceKey = {
          name: singleWorkspace.name ?? '',
          branchKey,
        }
        await rebaseWorkspace(workspaceKey)
      }
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    }
  }

  return (
    <OperationsLoadedButton
      startIcon="Eye"
      onClick={handleCatchup}
      isLoading={isCatchingUp}
      disabled={isProtectedBranchCategory(currentConciseBranch?.branchCategory)}
    >
      {translate('branchDetailsPanel.tabs.manage.manageFollows')}
    </OperationsLoadedButton>
  )
}
